(() => {
  const SELECTOR = '[data-veiled-collections-slider]';

  class VeiledCollectionsSlider {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-slider-viewport]');
      this.track = root.querySelector('[data-slider-track]');
      this.slides = Array.from(root.querySelectorAll('[data-slider-slide]'));
      this.prevButton = root.querySelector('[data-slider-prev]');
      this.nextButton = root.querySelector('[data-slider-next]');
      this.dots = Array.from(root.querySelectorAll('[data-slider-dot]'));
      this.counter = root.querySelector('[data-slider-counter]');
      this.status = root.querySelector('[data-slider-status]');
      this.index = 0;
      this.timer = null;
      this.scrollRaf = null;
      this.resizeObserver = null;

      this.loop = root.dataset.loop === 'true';
      this.autoplay = root.dataset.autoplay === 'true';
      this.autoplaySpeed = Number(root.dataset.autoplaySpeed) || 5000;
      this.scrollBehavior = root.dataset.scrollBehavior || 'smooth';
      this.pauseOnHover = root.dataset.pauseHover === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onPrev = this.goPrev.bind(this);
      this.onNext = this.goNext.bind(this);
      this.onScroll = this.handleScroll.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onMouseEnter = this.stopAutoplay.bind(this);
      this.onMouseLeave = this.startAutoplay.bind(this);
      this.onFocusIn = this.stopAutoplay.bind(this);
      this.onFocusOut = this.startAutoplay.bind(this);
    }

    init() {
      if (!this.root || !this.viewport || !this.slides.length) return;

      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('keydown', this.onKeydown);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const nextIndex = Number(dot.dataset.sliderDot);
          this.goTo(nextIndex);
        });
      });

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', this.onMouseEnter);
        this.root.addEventListener('mouseleave', this.onMouseLeave);
        this.root.addEventListener('focusin', this.onFocusIn);
        this.root.addEventListener('focusout', this.onFocusOut);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateFromScroll());
        this.resizeObserver.observe(this.viewport);
      }

      this.update(0);
      this.startAutoplay();
    }

    destroy() {
      this.stopAutoplay();
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.viewport?.removeEventListener('keydown', this.onKeydown);
      this.root?.removeEventListener('mouseenter', this.onMouseEnter);
      this.root?.removeEventListener('mouseleave', this.onMouseLeave);
      this.root?.removeEventListener('focusin', this.onFocusIn);
      this.root?.removeEventListener('focusout', this.onFocusOut);
      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.scrollRaf);
    }

    goPrev() {
      this.goTo(this.index - 1);
    }

    goNext() {
      this.goTo(this.index + 1);
    }

    goTo(rawIndex) {
      if (!this.slides.length) return;

      let nextIndex = rawIndex;

      if (this.loop) {
        nextIndex = (rawIndex + this.slides.length) % this.slides.length;
      } else {
        nextIndex = Math.max(0, Math.min(rawIndex, this.slides.length - 1));
      }

      const slide = this.slides[nextIndex];
      if (!slide) return;

      const viewportLeft = this.viewport.getBoundingClientRect().left;
      const slideLeft = slide.getBoundingClientRect().left;
      const targetLeft = this.viewport.scrollLeft + (slideLeft - viewportLeft);

      this.viewport.scrollTo({
        left: targetLeft,
        behavior: this.reducedMotion ? 'auto' : this.scrollBehavior
      });

      this.update(nextIndex);
      this.restartAutoplay();
    }

    handleScroll() {
      window.cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = window.requestAnimationFrame(() => this.updateFromScroll());
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goPrev();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goNext();
      }
    }

    updateFromScroll() {
      if (!this.slides.length) return;

      const viewportLeft = this.viewport.getBoundingClientRect().left;
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - viewportLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== this.index) {
        this.update(closestIndex);
      } else {
        this.update(this.index);
      }
    }

    update(index) {
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      const total = this.slides.length;
      const currentSlide = this.slides[this.index];
      const title = currentSlide?.dataset.slideTitle || `Slide ${this.index + 1}`;
      const atStart = this.index === 0;
      const atEnd = this.index === total - 1;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (this.counter) {
        this.counter.textContent = `${this.index + 1} / ${total}`;
      }

      if (this.status) {
        this.status.textContent = `Showing collection ${this.index + 1} of ${total}: ${title}`;
      }

      if (this.prevButton) {
        const disabled = !this.loop && atStart;
        this.prevButton.disabled = disabled;
        this.prevButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        this.prevButton.setAttribute('aria-label', disabled ? 'Previous collection unavailable. You are on the first collection.' : `Previous collection. Current collection: ${title}.`);
      }

      if (this.nextButton) {
        const disabled = !this.loop && atEnd;
        this.nextButton.disabled = disabled;
        this.nextButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        this.nextButton.setAttribute('aria-label', disabled ? 'Next collection unavailable. You are on the last collection.' : `Next collection. Current collection: ${title}.`);
      }
    }

    startAutoplay() {
      if (!this.autoplay || this.reducedMotion || this.slides.length < 2) return;
      this.stopAutoplay();
      this.timer = window.setInterval(() => {
        if (!this.loop && this.index >= this.slides.length - 1) {
          this.stopAutoplay();
          return;
        }
        this.goTo(this.index + 1);
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  function initRoot(root) {
    if (!root || root.__veiledCollectionsSlider) return;
    const instance = new VeiledCollectionsSlider(root);
    root.__veiledCollectionsSlider = instance;
    instance.init();
  }

  function destroyRoot(root) {
    if (!root?.__veiledCollectionsSlider) return;
    root.__veiledCollectionsSlider.destroy();
    root.__veiledCollectionsSlider = null;
  }

  function initAll(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach(initRoot);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTOR).forEach(destroyRoot);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-slider-slide]');
    const root = event.target.closest(SELECTOR);
    if (!slide || !root?.__veiledCollectionsSlider) return;
    root.__veiledCollectionsSlider.stopAutoplay();
    root.__veiledCollectionsSlider.goTo(Number(slide.dataset.slideIndex));
  });
})();
